import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, styled } from '@mui/material';
import { DateTime } from 'luxon';

import { AppDispatch } from '@/data/store';
import type { CreateReservationLocationState } from './schema/duplicateReservation.model';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useFeatureDuplicateReservationForCurrentPortal } from '@/hooks/features/useFeatureDuplicateReservation';

import {
  fetchPassengerClientDataAsync,
  selectPassengerClientData,
} from '@/data/client.slice';
import {
  fetchPassengerAsync,
  fetchPassengerPreferencesAsync,
  selectCurrentPassenger,
  selectLoadedCurrentPassenger,
} from '@/data/passenger.slice';
import { fetchPassengerBenefitSummaryAsync } from '@/data/reservation.slice';
import * as accountingSlice from '@/data/accounting.slice';
import { selectTransportationAttributes } from '@/data/accounting.slice';

import { CreateReservationDialog } from './CreateReservationDialog';

const PREFIX = 'CreateReservationPage';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
  },
}));

const CreateReservationPage = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const duplicateReservationEnabled =
    useFeatureDuplicateReservationForCurrentPortal();
  // History keeps router state, so re-check the flag rather than trusting it.
  const duplicateSource = duplicateReservationEnabled
    ? (location.state as CreateReservationLocationState | null)?.duplicateSource
    : undefined;
  const passenger = useAppSelector(selectCurrentPassenger);
  const loadedCurrentPassenger = useAppSelector(selectLoadedCurrentPassenger);
  const passengerClientData = useAppSelector(selectPassengerClientData);
  const transportationAttributes = useAppSelector(
    selectTransportationAttributes,
  );

  /**
   * Permissions calculations
   */
  const canModifyPickupTime = true; // True for all F1 portal users

  const handleClose = () => {
    navigate(`/passengers/${params.id}`);
  };

  useEffect(() => {
    dispatch(accountingSlice.fetchTransportationAttributesAsync());

    if (params.id && passenger?.passengerUUID !== params.id) {
      dispatch(fetchPassengerAsync(params.id));
    }
    if (
      params.id &&
      passenger?.passengerUUID &&
      params.id === passenger.passengerUUID
    ) {
      dispatch(
        fetchPassengerBenefitSummaryAsync(
          passenger.passengerUUID,
          passenger.servicePlans.map(({ servicePlanUUID }) => servicePlanUUID),
          DateTime.now().toISODate(),
        ),
      );
      dispatch(
        fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
          passenger.passengerUUID,
        ),
      );
      dispatch(
        fetchPassengerPreferencesAsync(
          passenger.passengerUUID,
          passenger.clientBC,
        ),
      );
    }
  }, [params.id, passenger?.passengerUUID, dispatch]);

  useEffect(() => {
    if (loadedCurrentPassenger && passenger) {
      dispatch(
        fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
          passenger.passengerUUID,
        ),
      );
    }
  }, [loadedCurrentPassenger, passenger]);

  return (
    <StyledBox className={classes.container}>
      <CreateReservationDialog
        open={true}
        onClose={handleClose}
        passenger={passenger}
        servicePlans={passengerClientData?.servicePlans ?? []}
        transportationAttributes={transportationAttributes}
        duplicateSource={duplicateSource}
      />
    </StyledBox>
  );
};

export { CreateReservationPage };
